import EventManager from '../eventManager/EventManager'
import WebModel from '../models/WebModel'
import { IBidResponse } from '../../typings/IPrebid'
import SlotRepository from '../slots/SlotRepository'
import { ISlotTargeting } from '../slots/Slot'
import { AdType, IAdType } from '../enums/AdType'
import { isNativeAd } from '../prebidFacade/BidderSettings'
import { renderAdSafeframe, renderAdNonSafeframe } from './renderAd'
import { Bidder } from '../enums'

enum MessageType {
  creativeDelivery = 'creativeDelivery'
}

export default class InjectableCreative {
  private bidsByAdId: { [adId: string]: IBidResponse } = {}

  constructor(readonly model: WebModel, pbjs = window.pbjs) {
    addEventListener(
      'message',
      (event) => this.listenAdRequestFromCreative(event),
      false
    )
    pbjs.onEvent(EventManager.events.bidResponse, (bid: IBidResponse) => {
      this.bidsByAdId[bid.adId] = bid
    })
  }

  /** This event occurs after the slot render ended event */
  private listenAdRequestFromCreative(event): void {
    // Get data from message
    let data: { message: string; targeting: string }
    let targeting: { [key in keyof ISlotTargeting]: Array<string> }
    try {
      data = JSON.parse(event.data)
      // Check if from our code
      if (data.message !== 'requestFunction') {
        return
      }

      targeting = JSON.parse(data.targeting)
    } catch {
      return
    }

    const adId = (targeting.hb_adid && targeting.hb_adid[0]) || ''
    const slotId = (targeting.slot_id && targeting.slot_id[0]) || ''

    // Get requested bidResponse
    const bidResponse = this.bidsByAdId[adId]
    const slot = SlotRepository.getSlotById(slotId)

    if (!bidResponse || !slot) {
      console.log('missing bid response or slot')
      return
    }

    slot.adType = getPrebidAdType(bidResponse)
    slot.height = bidResponse.height
    slot.width = bidResponse.width
    slot.sizeContainers()

    // We're skipping Prebid's renderAd function which emits the `bidWon` event
    // so let's emit 'bidWon' event here
    EventManager.trigger(EventManager.events.bidWon, bidResponse)

    /* 
    Create our Function Message.
    The functionStr is our encoded, stringified function to execute.
    Arguments needed to execute are in the args array.
    Our typing enforces the correct arguments are given to execute the 
    specified function.
    */
    const renderingFunction =
      targeting.hb_safeframe && targeting.hb_safeframe[0] === '0'
        ? renderAdNonSafeframe
        : renderAdSafeframe
    const message: IFunctionMessage<
      typeof renderAdSafeframe | typeof renderAdNonSafeframe
    > = {
      args: [bidResponse],
      functionStr: encodeURI(renderingFunction.toString()),
      type: MessageType.creativeDelivery
    }

    /* Send it to the iframe to be executed */
    sendFunctionMessageToSource(event, message)
  }
}

/* 
  T is the function being stringified.
*/
function sendFunctionMessageToSource<T extends (...args) => any>(
  { source }: { source: Window | ServiceWorker | MessagePort | null },
  message: IFunctionMessage<T>
): void {
  if (!source) {
    return
  }
  // @ts-ignore
  source.postMessage(JSON.stringify(message), '*')
}

interface IMessage {
  type: MessageType
}

/* Where T is the function being stringified */
interface IFunctionMessage<T extends (...args) => any> extends IMessage {
  functionStr: string
  type: MessageType.creativeDelivery
  args: Parameters<T>
}

function getPrebidAdType(bidResponse: IBidResponse): IAdType {
  if (isNativeAd(bidResponse)) {
    return AdType.prebidNative as IAdType
  }

  return AdType.prebidStandard as IAdType
}
